import {
  ICreateUserRequest,
  ICreateUserResponse,
  AddressValidateReq,
  AddressValidateRes,
  UpdateUserReq,
  UpdateUserRes,
  ICheckEmailExistResponse,
  IGetUserProfileResponse,
  InitiatePhoneNumberUpdateReq,
  InitiatePhoneNumberUpdateRes,
  GetAccountSummaryResponse,
  ExistingUserResponse,
} from '@root/types/User'
import { https } from '@root/utils'
import { httpsClient } from '@root/utils/httpsClient'

/**
 * @usage: For the Lite flow
 * @description: Create MoneyLion Lite account
 * @returns {ICreateUserResponse}: Returns the user profile
 */
const postCreateUser = async (
  request: ICreateUserRequest
): Promise<ICreateUserResponse> => {
  const httpsInit = await https({
    shouldAddRequestId: true,
    shouldCheckToken: false,
    errorTitle: '[userApi - postCreateUser]',
  })

  const payload = {
    ...request,
  }

  const response = await httpsInit.post<ICreateUserResponse>(
    `/user/me`,
    payload
  )

  return response.data
}

const checkEmailExist = async (
  email: string
): Promise<ICheckEmailExistResponse> => {
  // This endpoint does not require authentication
  const response = await (
    await https({
      shouldCheckToken: false,
      shouldAddRequestId: true,
      errorTitle: '[checkEmailExist]',
    })
  ).get(`/user/email?email=${email}`)

  return response.data
}

const checkEmailExistV2 = async (
  email: string
): Promise<ExistingUserResponse | null> => {
  const params = new URLSearchParams({
    email,
  })

  // This endpoint does not require authentication
  const httpsInit = await https({
    shouldCheckToken: false,
    shouldAddRequestId: true,
    errorTitle: '[MPL - checkEmailExistV2]',
    expectErrorData: true,
  })

  try {
    const response = await httpsInit.get(`/user/email/mailable?${params}`)
    const result = response.data
    if (response.status === 200) {
      return result
    }
    throw new Error('Error validate user email')
  } catch (err) {
    return null
  }
}

const getUserProfile = async (
  withSsn?: boolean
): Promise<IGetUserProfileResponse> => {
  const resp = await (
    await https({ errorTitle: '[getUserProfile]' })
  ).get('/user/getProfile', {
    params: { withProfilePicturePreview: true, withSsn },
  })
  return resp.data as IGetUserProfileResponse
}

const validateAddress = async (
  payload: AddressValidateReq
): Promise<AddressValidateRes> => {
  const response = await (
    await https({ errorTitle: '[validateAddress]' })
  ).post('user/address/v2/validate', payload)
  return response.data
}

const updateUser = async (payload: UpdateUserReq): Promise<UpdateUserRes> => {
  const response = await (
    await https({ expectErrorData: true, errorTitle: '[updateUser]' })
  ).put(`user/me`, payload)
  return response.data
}

/**
 * To challenge and update phone number
 */
const challengeAndUpdatePhoneNumber = async (
  body: InitiatePhoneNumberUpdateReq
): Promise<InitiatePhoneNumberUpdateRes> => {
  const response = await (
    await https({ errorTitle: '[challengeAndUpdatePhoneNumber]' })
  ).post(`user/initiatePhoneNumber/update`, body)

  return response.data
}

/**
 * To get account summary
 */
const getAccountSummary = async (): Promise<GetAccountSummaryResponse> => {
  const response = await httpsClient(`/user/getAccountSummary`, {
    method: 'GET',
    errorTitle: '[getAccountSummary]',
  })

  return response.data
}

const userApi = {
  postCreateUser,
  checkEmailExist,
  getUserProfile,
  validateAddress,
  updateUser,
  challengeAndUpdatePhoneNumber,
  getAccountSummary,
  checkEmailExistV2,
}

export default userApi
